import { registerActionImpl } from "../actions/registry";
import { setParentOrigin } from "./parentOrigin";
/**
 * Iframe-mode action implementations + the low-level postMessage transport
 * that wishlist/identity/etc. use to round-trip through the parent page's
 * `host-bridge.js` (shipped by the Tapcart Wishlist Shopify Theme App
 * Extension).
 *
 * When the bridge is rendered inside a cross-origin iframe (i.e. embedded
 * on a merchant's Shopify storefront), it can't directly:
 *   - Mutate the storefront cart, navigate the parent, or redirect to
 *     /account/login (same-origin policy).
 *   - Fetch Tapcart's App Proxy URLs (`/apps/tapcart/*` is on the merchant's
 *     origin; from the iframe's Tapcart origin that's cross-origin and
 *     Shopify's App Proxy signing layer never sees the request).
 *
 * Both are solved the same way: the iframe posts a typed envelope to its
 * parent, the parent's host-bridge.js runs the same-origin work and posts
 * the response back. The transport is shared between same-origin actions
 * (handled here directly) and App Proxy operations (delegated via
 * `sendToHost` from `appProxy.ts`).
 */
const REQUEST_TIMEOUT_MS = 10000;
const generateRequestId = () => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return `req_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};
const isHostBridgeResponse = (data) => {
    if (!data || typeof data !== "object")
        return false;
    const d = data;
    return d.source === "tapcart-host-bridge" && typeof d.requestId === "string";
};
/**
 * Low-level postMessage transport. Sends an envelope to the parent window
 * and invokes the appropriate handler callback when the parent responds.
 * On timeout, calls onError with a timeout Error.
 *
 * Used by:
 *   - `postMessageAction` (same-origin actions like cart/add)
 *   - `requestFromHost` (Promise wrapper for identity bootstrap)
 *   - `appProxy.ts` (when running in iframe — routes wishlist ops here
 *     instead of fetching directly)
 *
 * Returns void; results arrive via `handlers.onSuccess` / `handlers.onError`.
 */
export const sendToHost = (actionType, data, handlers) => {
    var _a;
    if (typeof window === "undefined" || window.parent === window) {
        (_a = handlers.onError) === null || _a === void 0 ? void 0 : _a.call(handlers, new Error(`[storefront-bridge] sendToHost("${actionType}") called but not running in an iframe`));
        return;
    }
    const requestId = generateRequestId();
    let settled = false;
    const onResponse = (event) => {
        var _a, _b;
        // Trust-boundary check: only the parent window can respond to our
        // postMessage. Without this, ANY same-page iframe, embedded script, or
        // browser extension can post a forged {source, requestId, payload}
        // envelope and have `customerId` (or wishlist data) stamped onto the
        // bridge as if Shopify had verified it. The envelope shape is public
        // surface — sender identity is the only real check we have.
        if (event.source !== window.parent)
            return;
        // Verified to come from the parent storefront — its origin is the
        // storefront origin, captured for screen/open's "Back to Shop" nav.
        setParentOrigin(event.origin);
        if (!isHostBridgeResponse(event.data))
            return;
        if (event.data.requestId !== requestId)
            return;
        if (settled)
            return;
        settled = true;
        window.removeEventListener("message", onResponse);
        window.clearTimeout(timeoutId);
        if (event.data.status === "ok") {
            (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, event.data.payload);
        }
        else {
            (_b = handlers.onError) === null || _b === void 0 ? void 0 : _b.call(handlers, event.data.payload);
        }
    };
    window.addEventListener("message", onResponse);
    const timeoutId = window.setTimeout(() => {
        var _a;
        if (settled)
            return;
        settled = true;
        window.removeEventListener("message", onResponse);
        (_a = handlers.onError) === null || _a === void 0 ? void 0 : _a.call(handlers, new Error(`[storefront-bridge] "${actionType}" timed out after ${REQUEST_TIMEOUT_MS}ms; host-bridge.js not installed?`));
    }, REQUEST_TIMEOUT_MS);
    // The parent is on the merchant's storefront origin which we don't know
    // at compile time. Using "*" is acceptable because the envelope is
    // namespaced (`source: "tapcart-storefront-bridge"`) and the host bridge
    // verifies our iframe origin before processing.
    window.parent.postMessage({
        source: "tapcart-storefront-bridge",
        type: actionType,
        requestId,
        payload: data,
    }, "*");
};
/**
 * Promise wrapper around `sendToHost`. Convenient for one-off async
 * operations like identity bootstrap where await semantics are clearer
 * than callbacks.
 */
export const requestFromHost = (actionType, data) => {
    return new Promise((resolve, reject) => {
        sendToHost(actionType, data, {
            onSuccess: (payload) => resolve(payload),
            onError: (err) => reject(err),
        });
    });
};
/**
 * Ask the host page (via `host-bridge.js`) for the verified Shopify
 * customer ID. Used by `StorefrontBridgeProvider` on iframe mount to
 * populate `variables.customer`, since the iframe itself can't see the
 * `logged_in_customer_id` Shopify forwards through the App Proxy.
 *
 * Returns `{ customerId: null }` for guests (NOT an error — the host
 * bridge passes the App Proxy response straight through).
 */
export const fetchIdentityFromHost = () => requestFromHost("tapcart/identity", null);
/**
 * Action factory that just postMessages the action type to the parent
 * and forwards the host's response. Used for the three actions that
 * can't run from a cross-origin iframe (cart, product nav, auth) — see
 * `installIframeActions` for the registry overrides.
 */
const postMessageAction = (actionType) => {
    return (data, handlers) => sendToHost(actionType, data, handlers);
};
/**
 * Detect whether the current window is embedded as an iframe. Returns
 * false in non-browser contexts (SSR), in non-iframe browser windows,
 * and in the rare cross-origin-throw case (defensive — should not
 * happen since same-origin check is cheap).
 */
export const isInIframe = () => {
    if (typeof window === "undefined")
        return false;
    try {
        return window.parent !== window;
    }
    catch (_a) {
        return true;
    }
};
/**
 * Register postMessage-backed implementations for the actions that act on
 * the PARENT storefront and so cannot run from a cross-origin iframe
 * (cart, product nav, collection nav, auth redirect).
 *
 * Wishlist actions are NOT registered here — they dispatch through
 * `appProxy.ts` which checks `isInIframe()` itself and routes via
 * `sendToHost` when needed. That keeps the wishlist dispatcher logic
 * single-sourced and lets the top-level browser code path stay
 * equivalent to the iframe code path at the action-handler layer.
 *
 * `screen/open` and `go/back` are intentionally NOT overridden: they
 * navigate WITHIN the Tapcart surface (the iframe IS that surface), so
 * `window.location` / `history.back()` already do the right thing in iframe
 * mode — they move the iframe to another Tapcart screen, not the parent.
 * Overriding them to postMessage the parent would wrongly navigate the
 * merchant's storefront. (External-URL destinations from screen/open open a
 * new tab via `window.open`, which is allowed from an iframe.)
 *
 * Idempotent — safe to call on every mount.
 */
export const installIframeActions = () => {
    registerActionImpl("cart/add", postMessageAction("cart/add"));
    registerActionImpl("product/open", postMessageAction("product/open"));
    registerActionImpl("collection/open", postMessageAction("collection/open"));
    registerActionImpl("authentication/open", postMessageAction("authentication/open"));
    return () => {
        // No-op: module-level singleton registry; previous impls not captured.
    };
};
